import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MetricsHistoryResponse } from '../../types';
import { metricsService } from '../../services/api';

// Time-window history charts for the Status page. Every series comes from
// the platform's own database (recorder heartbeat samples + the lifecycle
// ledger) — no cloud monitoring service involved.
//
// Chart styling follows the dataviz method: single-series small multiples
// (title carries identity, no legends), 2px lines / rounded-top bars with
// gaps, recessive hairline grid, muted ink for axes, and a crosshair +
// tooltip hover layer. Series colors validated against the white card
// surface: blue #2a78d6 (gauges) and brand gold #C8862F (counts).

const WINDOW_PRESETS = ['1h', '3h', '6h', '12h', '1d', '2d', '1w', '2w', '1mo'] as const;
const LINE_COLOR = '#2a78d6';
const BAR_COLOR = '#C8862F';

const GRID_COLOR = '#E1E2EA';   // viewer --rule (hairline)
const AXIS_INK = '#898781';     // muted axis ink
const INK = '#1E2130';          // viewer --ink

interface SeriesPoint {
  t: number;
  v: number | null;
}

const formatBytesPerSec = (v: number): string => {
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB/s`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB/s`;
  return `${Math.round(v)} B/s`;
};

const formatTick = (epoch: number, spanSeconds: number): string => {
  const d = new Date(epoch * 1000);
  if (spanSeconds <= 2 * 86400) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatFull = (epoch: number): string => new Date(epoch * 1000).toLocaleString();

interface MiniChartProps {
  title: string;
  points: SeriesPoint[];
  kind: 'line' | 'bar';
  color: string;
  fromEpoch: number;
  toEpoch: number;
  bucketSeconds: number;
  formatValue: (v: number) => string;
  // Fixed upper bound (e.g. 100 for percentages); otherwise data max.
  yMax?: number;
  // How often the source is sampled, when that is coarser than the bucket.
  // A gap is a missing SAMPLE, not a missing bucket: a once-a-minute series
  // drawn in 10-second buckets has five empty buckets between every pair of
  // points, and judged by bucket width alone it would be all gaps and no line.
  sampleSeconds?: number;
  // Grid tile (default) vs the single-chart expanded view: a wider, taller
  // canvas with more x-ticks. The SVG itself always stretches to its
  // container's width.
  expanded?: boolean;
  onExpand?: () => void;
}

const PAD = { left: 52, right: 10, top: 10, bottom: 24 };

const MiniChart: React.FC<MiniChartProps> = ({
  title, points, kind, color, fromEpoch, toEpoch, bucketSeconds, formatValue, yMax,
  sampleSeconds, expanded = false, onExpand,
}) => {
  const [hover, setHover] = useState<SeriesPoint | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const W = expanded ? 1280 : 560;
  const H = expanded ? 400 : 170;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const span = toEpoch - fromEpoch;

  const dataMax = useMemo(() => {
    let max = 0;
    for (const p of points) {
      if (p.v !== null && p.v > max) max = p.v;
    }
    return max;
  }, [points]);
  const top = yMax !== undefined ? yMax : (dataMax > 0 ? dataMax * 1.1 : 1);

  const x = (t: number) => PAD.left + ((t - fromEpoch) / span) * plotW;
  const y = (v: number) => PAD.top + plotH - (Math.min(v, top) / top) * plotH;

  // Line path with breaks at gaps (missing samples = source down). Points
  // with no neighbour on either side cannot form a segment, so they are
  // returned separately and drawn as dots — otherwise a lone sample, or a
  // series that has only just started, renders as nothing at all.
  const gapSeconds = Math.max(bucketSeconds, sampleSeconds ?? 0) * 1.5;
  const [linePath, lonePoints] = useMemo(() => {
    if (kind !== 'line') return ['', [] as SeriesPoint[]];
    let d = '';
    const lone: SeriesPoint[] = [];
    const present = points.filter(p => p.v !== null);
    present.forEach((p, i) => {
      const joinsPrev = i > 0 && p.t - present[i - 1].t <= gapSeconds;
      const joinsNext = i < present.length - 1 && present[i + 1].t - p.t <= gapSeconds;
      if (!joinsPrev && !joinsNext) lone.push(p);
      d += `${joinsPrev ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v as number).toFixed(1)}`;
    });
    return [d, lone];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, kind, fromEpoch, toEpoch, top, gapSeconds]);

  // Rounded-top bar anchored to the baseline, 2px gap between neighbors.
  const barPath = (p: SeriesPoint): string => {
    if (p.v === null || p.v <= 0) return '';
    const bw = Math.max(1, (bucketSeconds / span) * plotW - 2);
    const bx = x(p.t) + 1;
    const by = y(p.v);
    const base = PAD.top + plotH;
    const r = Math.min(4, bw / 2, base - by);
    return `M${bx},${base} V${by + r} Q${bx},${by} ${bx + r},${by} H${bx + bw - r} Q${bx + bw},${by} ${bx + bw},${by + r} V${base} Z`;
  };

  const yTicks = expanded ? [0, top / 4, top / 2, (3 * top) / 4, top] : [0, top / 2, top];
  const xTickCount = expanded ? 10 : 4;
  const xTicks = Array.from({ length: xTickCount + 1 }, (_, i) => fromEpoch + (span * i) / xTickCount);

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const t = fromEpoch + ((e.clientX - rect.left) / rect.width * W - PAD.left) / plotW * span;
    let nearest: SeriesPoint | null = null;
    for (const p of points) {
      if (p.v === null) continue;
      if (nearest === null || Math.abs(p.t + bucketSeconds / 2 - t) < Math.abs(nearest.t + bucketSeconds / 2 - t)) {
        nearest = p;
      }
    }
    setHover(nearest);
  };

  return (
    <div className={`metrics-chart ${expanded ? 'metrics-chart-expanded' : ''}`}>
      <div className="metrics-chart-title-row">
        <div className="metrics-chart-title">{title}</div>
        {onExpand && (
          <button
            type="button"
            className="metrics-expand-button"
            title={expanded ? 'Back to all charts' : 'Expand this chart'}
            onClick={onExpand}
          >
            {expanded ? '⤡ All charts' : '⤢'}
          </button>
        )}
      </div>
      <div className="metrics-chart-body">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={title}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)}
                stroke={GRID_COLOR} strokeWidth={1} />
              <text x={PAD.left - 6} y={y(v) + 3.5} textAnchor="end" fontSize={11}
                fill={AXIS_INK} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatValue(v)}
              </text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i}
              x={Math.min(Math.max(x(t), PAD.left + 14), W - PAD.right - 14)}
              y={H - 6} textAnchor="middle" fontSize={11} fill={AXIS_INK}
              style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatTick(t, span)}
            </text>
          ))}

          {kind === 'line'
            ? <>
                <path d={linePath} fill="none" stroke={color} strokeWidth={2}
                  strokeLinejoin="round" strokeLinecap="round" />
                {lonePoints.map(p => (
                  <circle key={p.t} cx={x(p.t)} cy={y(p.v as number)} r={2.5} fill={color} />
                ))}
              </>
            : points.map((p, i) => <path key={i} d={barPath(p)} fill={color} />)}

          {hover && hover.v !== null && (
            <g>
              <line x1={x(hover.t) + (kind === 'bar' ? (bucketSeconds / span) * plotW / 2 : 0)}
                x2={x(hover.t) + (kind === 'bar' ? (bucketSeconds / span) * plotW / 2 : 0)}
                y1={PAD.top} y2={PAD.top + plotH} stroke={AXIS_INK} strokeWidth={1}
                strokeDasharray="3,3" />
              {kind === 'line' && (
                <circle cx={x(hover.t)} cy={y(hover.v)} r={4.5} fill={color}
                  stroke="#FFFFFF" strokeWidth={2} />
              )}
            </g>
          )}
        </svg>
        {hover && hover.v !== null && (
          <div className="metrics-tooltip"
            style={{ left: `${((x(hover.t) / W) * 100)}%` }}>
            <div className="metrics-tooltip-time">{formatFull(hover.t)}</div>
            <div className="metrics-tooltip-value" style={{ color: INK }}>
              <span className="metrics-swatch" style={{ background: color }} />
              {formatValue(hover.v)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetricsHistory: React.FC = () => {
  const [preset, setPreset] = useState<string>('3h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customActive, setCustomActive] = useState(false);
  const [data, setData] = useState<MetricsHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // datetime-local (browser-local) -> UTC "YYYY-MM-DD HH:MM:SS"
  const toUtcSql = (local: string): string | null => {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 19).replace('T', ' ');
  };

  const fetchData = async () => {
    try {
      if (customActive) {
        const from = toUtcSql(customFrom);
        const to = toUtcSql(customTo);
        if (!from || !to) return;
        setData(await metricsService.history({ from, to }));
      } else {
        setData(await metricsService.history({ window: preset }));
      }
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch metrics history');
    }
  };

  useEffect(() => {
    fetchData();
    // Live refresh only for preset windows (they end "now"); an explicit
    // range is a fixed snapshot.
    if (!customActive) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customActive, customFrom, customTo]);

  const applyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (toUtcSql(customFrom) && toUtcSql(customTo)) {
      setCustomActive(true);
    }
  };

  const charts = useMemo(() => {
    if (!data) return null;
    const fromEpoch = Math.floor(new Date(data.from).getTime() / 1000);
    const toEpoch = Math.floor(new Date(data.to).getTime() / 1000);
    const bucket = data.bucket_seconds;

    const host = (field: 'cpu_percent' | 'load_avg_1m' | 'mem_used_percent'
      | 'net_rx_bytes_per_sec' | 'disk_used_percent' | 'vcon_disk_used_percent'
      | 'active_sessions' | 'mcp_requests'): SeriesPoint[] =>
      data.host.map(p => ({ t: p.t, v: p[field] as number | null }));

    // Ledger buckets are sparse (only non-zero rows come back); bars want
    // real zeros in between so quiet periods read as quiet, not missing.
    const ledger = (field: 'vcons_created' | 'lifecycle_events'): SeriesPoint[] => {
      const byT = new Map(data.ledger.map(p => [p.t, p[field]]));
      const out: SeriesPoint[] = [];
      for (let t = Math.floor(fromEpoch / bucket) * bucket; t < toEpoch; t += bucket) {
        out.push({ t, v: byT.get(t) ?? 0 });
      }
      return out;
    };

    const pct = (v: number) => `${Math.round(v)}%`;
    const num = (v: number) => `${Math.round(v)}`;

    // Only worth its own chart when it is genuinely a different volume:
    // samples predating the split carry null, and a shared filesystem
    // reports figures identical to the recordings volume.
    const vconDiskIsSeparate = data.host.some(
      p => p.vcon_disk_used_percent != null && p.vcon_disk_used_percent !== p.disk_used_percent
    );

    // Whisper availability: buckets without a sample stay missing, so a
    // sampler that was not running (Mac asleep) shows as a break, not as 0%.
    const sttUp: SeriesPoint[] = (data.stt ?? []).map(p => ({ t: p.t, v: p.up_percent }));
    // Transcriptions are sparse like the ledger; zero-filled the same way and
    // summed across engines (a switch mid-window still reads as one series).
    const transcriptionsByT = new Map<number, number>();
    for (const p of data.transcriptions ?? []) {
      transcriptionsByT.set(p.t, (transcriptionsByT.get(p.t) ?? 0) + p.n);
    }
    const transcriptions: SeriesPoint[] = [];
    for (let t = Math.floor(fromEpoch / bucket) * bucket; t < toEpoch; t += bucket) {
      transcriptions.push({ t, v: transcriptionsByT.get(t) ?? 0 });
    }
    const engines = Array.from(new Set((data.transcriptions ?? []).map(p => p.engine)));

    return { fromEpoch, toEpoch, bucket, host, ledger, pct, num, vconDiskIsSeparate,
      sttUp, transcriptions, transcriptionsByT, engines };
  }, [data]);

  return (
    <div className="status-container">
      <h3>Metrics history</h3>
      <p className="settings-hint">
        Host and platform metrics over time, charted from the recorder's own 10-second
        heartbeat samples and the vCon lifecycle ledger — all stored on this server.
      </p>
      <div className="metrics-controls">
        {WINDOW_PRESETS.map(w => (
          <button
            key={w}
            type="button"
            className={`nav-button ${!customActive && preset === w ? 'active' : ''}`}
            onClick={() => { setCustomActive(false); setPreset(w); }}
          >
            {w}
          </button>
        ))}
        <form onSubmit={applyCustom} className="metrics-custom-range">
          <input type="datetime-local" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
          <span>→</span>
          <input type="datetime-local" value={customTo} onChange={e => setCustomTo(e.target.value)} />
          <button type="submit" className={`nav-button ${customActive ? 'active' : ''}`}>Apply</button>
        </form>
        <div className="metrics-refresh">
          <button type="button" className="nav-button" onClick={fetchData} title="Fetch the latest data now">
            Refresh
          </button>
          <span className="metrics-updated" title={customActive
            ? 'Explicit ranges are a fixed snapshot — refresh manually'
            : 'Preset windows auto-refresh every 30 seconds'}>
            {lastUpdated ? `updated ${lastUpdated.toLocaleTimeString()}` : ''}
            {!customActive && ' · auto 30s'}
          </span>
          <button type="button" className="nav-button metrics-table-toggle" onClick={() => setShowTable(s => !s)}>
            {showTable ? 'Charts' : 'Table'}
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {data && charts && data.host.length === 0 && data.ledger.length === 0 && (
        <p className="settings-hint">
          No data in this range. Host samples are written by the recorder's heartbeat —
          if the recorder was down (or predates metrics sampling) the range is empty.
        </p>
      )}
      {data && charts && !showTable && (() => {
        const configs: {
          key: string; title: string; points: SeriesPoint[]; kind: 'line' | 'bar';
          color: string; formatValue: (v: number) => string; yMax?: number; sampleSeconds?: number;
        }[] = [
          { key: 'cpu', title: 'CPU utilization', points: charts.host('cpu_percent'), kind: 'line', color: LINE_COLOR, formatValue: charts.pct, yMax: 100 },
          { key: 'load', title: 'Load average (1m)', points: charts.host('load_avg_1m'), kind: 'line', color: LINE_COLOR, formatValue: v => v.toFixed(2) },
          { key: 'mem', title: 'Memory used', points: charts.host('mem_used_percent'), kind: 'line', color: LINE_COLOR, formatValue: charts.pct, yMax: 100 },
          { key: 'net', title: 'Network RX', points: charts.host('net_rx_bytes_per_sec'), kind: 'line', color: LINE_COLOR, formatValue: formatBytesPerSec },
          { key: 'disk', title: 'Disk used (recordings volume)', points: charts.host('disk_used_percent'), kind: 'line', color: LINE_COLOR, formatValue: charts.pct, yMax: 100 },
          // A small multiple rather than a second line on the chart above:
          // same units and scale, so the pair reads as a comparison. Shown
          // only when the vCon store is actually a different volume —
          // otherwise it would be a duplicate of the chart before it.
          ...(charts.vconDiskIsSeparate
            ? [{ key: 'vcondisk', title: 'Disk used (vCon volume)', points: charts.host('vcon_disk_used_percent'), kind: 'line' as const, color: LINE_COLOR, formatValue: charts.pct, yMax: 100 }]
            : []),
          { key: 'sessions', title: 'Active SIPREC sessions', points: charts.host('active_sessions'), kind: 'line', color: LINE_COLOR, formatValue: charts.num },
          { key: 'vcons', title: 'vCons created', points: charts.ledger('vcons_created'), kind: 'bar', color: BAR_COLOR, formatValue: charts.num },
          { key: 'mcp', title: 'MCP requests', points: charts.host('mcp_requests'), kind: 'bar', color: BAR_COLOR, formatValue: charts.num },
          { key: 'events', title: 'Lifecycle events', points: charts.ledger('lifecycle_events'), kind: 'bar', color: BAR_COLOR, formatValue: charts.num },
          // Only on a host that samples a local engine.
          ...(charts.sttUp.length > 0
            ? [{ key: 'whisper', title: 'Whisper server availability', points: charts.sttUp, kind: 'line' as const, color: LINE_COLOR, formatValue: charts.pct, yMax: 100, sampleSeconds: 60 }]
            : []),
          ...(charts.sttUp.length > 0 || charts.engines.length > 0
            ? [{ key: 'transcriptions', title: `Transcriptions completed${charts.engines.length ? ` (${charts.engines.join(', ')})` : ''}`, points: charts.transcriptions, kind: 'bar' as const, color: BAR_COLOR, formatValue: charts.num }]
            : []),
        ];
        const expanded = configs.find(c => c.key === expandedKey);
        if (expanded) {
          return (
            <MiniChart
              title={expanded.title} points={expanded.points} kind={expanded.kind}
              color={expanded.color} fromEpoch={charts.fromEpoch} toEpoch={charts.toEpoch}
              bucketSeconds={charts.bucket} formatValue={expanded.formatValue} yMax={expanded.yMax}
              sampleSeconds={expanded.sampleSeconds}
              expanded onExpand={() => setExpandedKey(null)}
            />
          );
        }
        return (
          <div className="metrics-grid">
            {configs.map(c => (
              <MiniChart
                key={c.key} title={c.title} points={c.points} kind={c.kind}
                color={c.color} fromEpoch={charts.fromEpoch} toEpoch={charts.toEpoch}
                bucketSeconds={charts.bucket} formatValue={c.formatValue} yMax={c.yMax}
                sampleSeconds={c.sampleSeconds}
                onExpand={() => setExpandedKey(c.key)}
              />
            ))}
          </div>
        );
      })()}
      {data && charts && showTable && (
        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Time (bucket)</th>
                <th>CPU %</th>
                <th>Load 1m</th>
                <th>Mem %</th>
                <th>Net RX</th>
                <th>Disk %</th>
                <th>vCon disk %</th>
                <th>Active</th>
                <th>MCP req</th>
                <th>vCons</th>
                <th>Events</th>
                <th>Whisper up</th>
                <th>Transcr.</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ledgerByT = new Map(data.ledger.map(p => [p.t, p]));
                const hostByT = new Map(data.host.map(p => [p.t, p]));
                const sttByT = new Map((data.stt ?? []).map(p => [p.t, p]));
                const ts = Array.from(new Set([...Array.from(hostByT.keys()), ...Array.from(ledgerByT.keys()),
                  ...Array.from(sttByT.keys()), ...Array.from(charts.transcriptionsByT.keys())])).sort((a, b) => b - a);
                if (ts.length === 0) {
                  return <tr><td colSpan={13} className="no-data">No data in this range</td></tr>;
                }
                return ts.map(t => {
                  const h = hostByT.get(t);
                  const l = ledgerByT.get(t);
                  return (
                    <tr key={t}>
                      <td className="small">{formatFull(t)}</td>
                      <td>{h?.cpu_percent != null ? `${h.cpu_percent.toFixed(1)}%` : '—'}</td>
                      <td>{h?.load_avg_1m != null ? h.load_avg_1m.toFixed(2) : '—'}</td>
                      <td>{h?.mem_used_percent != null ? `${h.mem_used_percent.toFixed(1)}%` : '—'}</td>
                      <td>{h?.net_rx_bytes_per_sec != null ? formatBytesPerSec(h.net_rx_bytes_per_sec) : '—'}</td>
                      <td>{h?.disk_used_percent != null ? `${h.disk_used_percent.toFixed(1)}%` : '—'}</td>
                      <td>{h?.vcon_disk_used_percent != null ? `${h.vcon_disk_used_percent.toFixed(1)}%` : '—'}</td>
                      <td>{h ? h.active_sessions : '—'}</td>
                      <td>{h ? h.mcp_requests : '—'}</td>
                      <td>{l ? l.vcons_created : 0}</td>
                      <td>{l ? l.lifecycle_events : 0}</td>
                      <td>{sttByT.get(t) ? `${sttByT.get(t)!.up_percent.toFixed(0)}%` : '—'}</td>
                      <td>{charts.transcriptionsByT.get(t) ?? 0}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MetricsHistory;
